import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { useLang } from "@/App";

const CONTENT = {
    ua: {
        title: "Політика конфіденційності",
        updated: "Останнє оновлення: 31 жовтня 2026",
        sections: [
            {
                h: "1. Які дані ми збираємо",
                p: "Kiber AI (веб-сайт і Android-застосунок) не збирає персональні дані без вашої згоди. Якщо ви входите через Google — ми зберігаємо лише ваш email, ім'я та фото профілю, які Google передає після вашої згоди, для синхронізації налаштувань між пристроями.",
            },
            {
                h: "2. Історія розмов",
                p: "Повідомлення чату зберігаються для роботи сервісу: на сервері — для відповідей ШІ, на вашому пристрої — в Android-застосунку (локально, нікуди не передаються окремо). Ви можете очистити історію в будь-який момент, видаливши дані застосунку або скинувши сесію на сайті.",
            },
            {
                h: "3. Дозволи Android-застосунку",
                p: "Застосунок запитує лише доступ до Інтернету. Голосовий ввід обробляється системним розпізнавачем мови Google на вашому пристрої — ми не записуємо і не зберігаємо ваш голос. Застосунок НЕ має доступу до контактів, дзвінків, мікрофона чи інших застосунків.",
            },
            {
                h: "4. ШІ-відповіді",
                p: "Тексти ваших повідомлень передаються до ШІ-моделі (OpenAI через захищений шлюз) виключно для генерації відповіді. Ми не продаємо і не передаємо ваші дані третім сторонам.",
            },
            {
                h: "5. Cookies",
                p: "Сайт використовує лише технічні cookies для входу (сесія) і localStorage для ваших налаштувань (тема, мова, персонаж). Рекламних трекерів немає.",
            },
            {
                h: "6. Видалення даних",
                p: "Щоб видалити всі дані: вийдіть з акаунта на сторінці Налаштування та очистіть дані сайту/застосунку. Для повного видалення акаунта напишіть нам — контакт нижче.",
            },
            {
                h: "7. Контакт",
                p: "Розробник: Petro Kaminniy. З питань конфіденційності звертайтесь через форму зворотного зв'язку на сайті або email, вказаний у Google Play Console.",
            },
        ],
    },
    en: {
        title: "Privacy Policy",
        updated: "Last updated: October 31, 2026",
        sections: [
            {
                h: "1. What data we collect",
                p: "Kiber AI (website and Android app) does not collect personal data without your consent. If you sign in with Google, we only store the email, name and profile picture that Google shares after your approval, to sync your settings across devices.",
            },
            {
                h: "2. Chat history",
                p: "Chat messages are stored to make the service work: on the server — to generate AI replies, and on your device — in the Android app (stored locally, never shared separately). You can clear history anytime by clearing app data or resetting the session on the site.",
            },
            {
                h: "3. Android app permissions",
                p: "The app requests Internet access only. Voice input is handled by Google's on-device speech recognizer — we never record or store your voice. The app does NOT access your contacts, calls, microphone or other apps.",
            },
            {
                h: "4. AI replies",
                p: "Your message text is sent to an AI model (OpenAI via a secure gateway) solely to generate a reply. We never sell or share your data with third parties.",
            },
            {
                h: "5. Cookies",
                p: "The site uses only technical cookies for sign-in (session) and localStorage for your settings (theme, language, persona). No advertising trackers.",
            },
            {
                h: "6. Data deletion",
                p: "To delete all data: sign out on the Settings page and clear the site/app data. For full account deletion, contact us using the contact below.",
            },
            {
                h: "7. Contact",
                p: "Developer: Petro Kaminniy. For privacy questions, reach out via the site's contact form or the email listed in the Google Play Console.",
            },
        ],
    },
};

export default function PrivacyPage() {
    const { lang } = useLang();
    const c = CONTENT[lang] || CONTENT.ua;
    return (
        <div className="relative pt-16 min-h-screen overflow-hidden" data-testid="privacy-page">
            <div className="kana kana-watermark absolute right-2 top-24 text-[8rem] leading-none pointer-events-none" aria-hidden="true">
                プライバシー
            </div>
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#49f5e8] flex items-center gap-3 mb-4">
                        <ShieldCheck size={14} />
                        KIBER AI // LEGAL
                        <span className="warning-stripes w-12 h-1.5 opacity-70" />
                    </div>
                    <h1 className="font-display font-bold uppercase tracking-wide text-3xl sm:text-4xl mb-2" data-testid="privacy-title">
                        {c.title}
                    </h1>
                    <p className="font-mono2 text-[10px] tracking-[0.2em] text-slate-500 mb-10">{c.updated}</p>
                    <div className="space-y-8">
                        {c.sections.map((s, i) => (
                            <motion.section
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="glow-card rounded-2xl p-6"
                                data-testid={`privacy-section-${i}`}
                            >
                                <h2 className="font-display font-bold text-sm tracking-wider text-[#fcee0a] mb-3">{s.h}</h2>
                                <p className="text-sm text-slate-300 leading-relaxed">{s.p}</p>
                            </motion.section>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
